import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import {
  Eye, EyeOff, Loader2, Shield, BarChart3,
  Monitor, KeyRound, ArrowRight, Mail, Lock, CheckCircle2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FormField } from '../shared/FormField';
import { useForm } from '../../hooks/useForm';
import { API_URL } from '../../services/apiClient';

interface LoginFormData {
  email: string;
  password: string;
}

const features = [
  {
    icon: Monitor,
    title: 'Asset Tracking',
    desc: 'Track every device from procurement to disposal with full lifecycle visibility.',
  },
  {
    icon: KeyRound,
    title: 'License Management',
    desc: 'Monitor software licenses, assignments, and renewal dates in one place.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reports',
    desc: 'Real-time dashboards with depreciation insights and utilisation trends.',
  },
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    values: formData,
    errors,
    handleChange,
    handleBlur,
    validateForm
  } = useForm({
    email: '',
    password: '',
  }, {
    email: { required: true, email: true },
    password: { required: true }
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const ssoError = (location.state as { ssoError?: string } | null)?.ssoError;
    if (ssoError) {
      setError(ssoError);
    }
  }, [location.state]);

  const handleSsoLogin = () => {
    window.location.href = `${API_URL}/auth/sso/login`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await authService.login(formData.email, formData.password);
      localStorage.setItem('token', response.token);
      // Persist user info (including role) for use in AppShell and guards
      localStorage.setItem('user', JSON.stringify(response.user));
      navigate('/dashboard');
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen overflow-x-hidden bg-white">
      {/* ───── Left branding panel (hidden below lg) ───── */}
      <div className="hidden lg:flex lg:w-[56%] relative overflow-hidden bg-gradient-to-br from-[#0b1330] via-[#132257] to-[#1d3fa8] text-white">
        {/* Mesh / grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] bg-[linear-gradient(rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.6)_1px,transparent_1px)] bg-[size:44px_44px]"
          aria-hidden="true"
        />

        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Top – Logo */}
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="QBA" className="h-11 object-contain brightness-0 invert" />
          </div>

          {/* Middle – Hero copy + preview */}
          <div className="space-y-10 max-w-lg">
            <div className="space-y-5">
              <h1 className="text-[2.65rem] leading-[1.12] font-extrabold tracking-tight">
                Manage your <span className="text-[#ff4fa8]">IT assets</span>
                <br />
                with <span className="text-[#ff4fa8]">confidence.</span>
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-md">
                A unified platform to track hardware, manage licenses, schedule maintenance,
                and generate compliance reports — all from one dashboard.
              </p>
              <div className="flex items-center gap-2 text-xs font-medium text-white/70">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#ff4fa8]" /> Secure
                </span>
                <span className="text-white/30">•</span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#ff4fa8]" /> Compliant
                </span>
                <span className="text-white/30">•</span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#ff4fa8]" /> Always available
                </span>
              </div>
            </div>

            {/* Feature grid — glassmorphism cards */}
            <div className="grid grid-cols-1 gap-3">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="group flex items-start gap-3.5 rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur-md p-4 transition-all duration-200 hover:bg-white/[0.1] hover:border-white/20 hover:-translate-y-0.5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 border border-white/10 transition-colors group-hover:bg-[#e91e8c]/20">
                    <f.icon className="h-[18px] w-[18px] text-[#ff8ac6]" strokeWidth={1.75} />
                  </div>
                  <div className="pt-0.5">
                    <p className="font-semibold text-sm text-white">{f.title}</p>
                    <p className="text-xs text-white/70 leading-relaxed mt-1">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom spacer for balance */}
          <div />
        </div>
      </div>

      {/* ───── Right login panel ───── */}
      <div className="flex w-full lg:w-[44%] items-center justify-center bg-muted p-6 sm:p-12 relative overflow-y-auto">
        {/* Subtle accent glow */}
        <div className="pointer-events-none absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 w-full max-w-sm">
          {/* Mobile-only logo */}
          <div className="flex items-center gap-3 lg:hidden mb-8">
            <img src="/logo.svg" alt="QBA" className="h-9 object-contain" />
          </div>

          {/* Elevated card */}
          <div className="rounded-2xl border border-border bg-white shadow-[0_8px_30px_-8px_rgba(15,23,42,0.12)] p-7 sm:p-8 space-y-7">
            {/* Brand mark + heading */}
            <div className="space-y-3">
              <div className="hidden lg:flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary shadow-sm">
                <img src="/logo.svg" alt="" className="h-5 w-5 object-contain brightness-0 invert" aria-hidden="true" />
              </div>
              <div className="space-y-1">
                <h2 className="text-2xl font-bold tracking-tight text-foreground">Welcome back</h2>
                <p className="text-sm text-muted-foreground">
                  Enter your credentials to access the dashboard
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                <svg className="h-4 w-4 mt-0.5 shrink-0" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 4a.75.75 0 011.5 0v3a.75.75 0 01-1.5 0V5zm.75 6.25a.75.75 0 100-1.5.75.75 0 000 1.5z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} autoComplete="off" className="space-y-5" noValidate>
              <FormField
                id="email"
                label="Email address"
                required
                error={errors.email}
              >
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <Input
                    type="email"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    disabled={loading}
                    autoComplete="off"
                    className="h-11 pl-9 bg-white shadow-sm"
                  />
                </div>
              </FormField>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-primary hover:text-primary/80 hover:underline underline-offset-2 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <FormField
                  id="password"
                  label="Password"
                  hideLabel
                  required
                  error={errors.password}
                >
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      onBlur={() => handleBlur('password')}
                      disabled={loading}
                      autoComplete="off"
                      className="h-11 pl-9 pr-10 bg-white shadow-sm"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FormField>
              </div>

              <label htmlFor="remember-me" className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                  className="h-4 w-4 rounded border-border text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                Remember me
              </label>

              <Button
                type="submit"
                className="w-full h-11 text-sm font-semibold gap-2 bg-gradient-to-r from-primary to-primary hover:from-primary hover:to-primary shadow-lg shadow-blue-600/20 transition-all"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in to dashboard
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-muted" />
              <span className="text-xs font-medium text-muted-foreground">OR</span>
              <div className="h-px flex-1 bg-muted" />
            </div>

            {/* Microsoft SSO */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 text-sm font-semibold gap-2"
              disabled={loading}
              onClick={handleSsoLogin}
            >
              <svg className="h-4 w-4" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </Button>

            {/* Security message */}
            <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <Shield className="h-3.5 w-3.5" aria-hidden="true" />
              Protected by industry-standard encryption
            </div>
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} IT Asset Management. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
